import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { library } from '@fortawesome/fontawesome-svg-core'
import { SettingsContext } from '../contexts/SettingsContext'
import { faCheck, faCircleQuestion, faFolder, faFolderMinus, faFolderPlus, faGhost, faICursor, faPen,
    faPersonCircleQuestion, faSignature, faTags, faTextSlash, faXmark } from '@fortawesome/free-solid-svg-icons'
import Loading from '../common/Loading'
import {
    AliasType, AttributeType, GenericSnippet, SettingsContextState, SnippetIdentifier, SnippetType
} from '../contexts/SettingsContext.d'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import snippetsConfig, { snippetModifyKey } from './SnippetsConfig'
import { countryFlag } from '../helpers/countries'
import { SettingsAction } from '../helpers/action/SettingsAction.d'
import { actionInProgress, conditionalDispatch } from '../helpers/action/action'
import { AppContext } from '../contexts/AppContext'
import { ReloadButton } from '../common/ReloadButton'
library.add(faSignature, faTags, faPersonCircleQuestion, faTextSlash, faGhost, faICursor, faXmark, faPen, faCheck,
    faFolder, faFolderPlus, faFolderMinus, faCircleQuestion)

const regexHelpMessage = 'Matched as a regular expression — click for a syntax reference.'

const SnippetsPage = (): ReactElement => {
    const { state } = useContext(SettingsContext)
    const header = <h1><FontAwesomeIcon icon="i-cursor" /> Text fine-tuning</h1>
    if (!state.snippets) {
        return <>
            {header}
            <Loading message="Retrieving your configuration..." />
        </>
    }
    return <>
        {header}
        <div className="thumbnail thumbnail-static snippets">
            <h2>
                {snippetsConfig.aliases.icon && <FontAwesomeIcon icon={ snippetsConfig.aliases.icon } />} {snippetsConfig.aliases.title}
                { state.snippets && <> <ReloadButton context={ SettingsContext } action={ { type: 'RETRIEVE_SNIPPETS' } } /></>}
            </h2>
            <div className="tip">{snippetsConfig.aliases.description}</div>
            <Snippets identifier="aliases" />
        </div>
        <div className="thumbnail thumbnail-static snippets">
            <h2><FontAwesomeIcon icon="magnifying-glass" /> Further fine-tuning</h2>
            <Snippets identifier="snippets" />
            <Snippets identifier="attributes" />
        </div>
    </>
}

const Snippets = ({ identifier }: { identifier: SnippetIdentifier }): ReactElement => {
    const types = Object.keys(snippetsConfig[identifier].types || {})
    return <>{types.map(type => <RenderSnippetType key={ type } type={ type } identifier={ identifier } />)}</>
}

const RenderSnippetType = ({ identifier, type }: { identifier: SnippetIdentifier, type: string }): ReactElement => {
    const { state } = useContext(SettingsContext)
    const snippets = state.snippets?.[identifier]?.[type] || {}
    const newSnippets = state.snippetsPending?.create?.[type] || {}
    const typeConfig = snippetsConfig[identifier].types?.[type]
    return <details className="snippet-type" open>
        <summary>
            <h3>{typeConfig?.icon && <FontAwesomeIcon icon={ typeConfig.icon } />} {typeConfig?.title || type}</h3>
        </summary>
        { typeConfig?.description && <div className="tip">{typeConfig.description}</div>}
        {identifier === 'attributes'
            ? Object.keys(snippets).map(snippet => <RenderGenericSnippet key={ snippet } snippet={ snippets[snippet] } />)
            : <RenderGroupedSnippets snippets={ snippets } />}
        {Object.keys(newSnippets)
            .filter(snippet => !('aliases' in newSnippets[snippet] && newSnippets[snippet].aliases.length > 0))
            .map(snippet => <RenderGenericSnippet key={ snippet } snippet={ { ...newSnippets[snippet], creating: true } } />)}
        <div className="clear-float" />
        <AddSnippetButton type={ type } identifier={ identifier } />
    </details>
}

// The snippet's group, preferring a not-yet-saved pending change so edits reflect instantly.
const snippetGroup = (snippet: GenericSnippet, state: SettingsContextState): string => {
    const saved = 'groupLabel' in snippet && snippet.groupLabel ? snippet.groupLabel : ''
    return state.snippetsPending?.modify?.[snippetModifyKey(snippet, saved)]?.newValue ?? saved
}

const RenderGroupedSnippets = ({ snippets }: { snippets: { [key: string]: GenericSnippet } }): ReactElement => {
    const { state } = useContext(SettingsContext)
    const groups: { [group: string]: string[] } = {}
    Object.keys(snippets).forEach((key) => {
        const group = snippetGroup(snippets[key], state)
        groups[group] = (groups[group] || []).concat([key])
    })
    const named = Object.keys(groups).filter(group => group !== '').sort((a, b) => a.localeCompare(b))
    return <>
        {named.map(name => <details key={ name } className="snippet-group" open>
            <summary>{name}</summary>
            {groups[name].map(key => <RenderGenericSnippet key={ key } snippet={ snippets[key] } />)}
            <div className="clear-float" />
        </details>)}
        {(groups[''] || []).map(key => <RenderGenericSnippet key={ key } snippet={ snippets[key] } />)}
    </>
}

const RenderGenericSnippet = ({ snippet }: { snippet: GenericSnippet }): ReactElement => {
    if ('canonical' in snippet && 'aliases' in snippet) {
        return <Alias alias={ snippet } />
    } else if ('key' in snippet && 'value' in snippet) {
        return <Attribute attribute={ snippet } />
    } else if ('str' in snippet) {
        return <Snippet snippet={ snippet } />
    }
    return <>Unknown snippet type!</>
}

const onModifyKeyDown = (saveModification: () => void, cancelModification: () => void) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
        saveModification()
    } else if (e.key === 'Escape') {
        cancelModification()
    }
}

const ModifiableSnippet = ({ snippet, field, index }: {
    snippet: GenericSnippet
    field: 'canonical' | 'alias' | 'str' | 'key' | 'value'
    index?: number
}): ReactElement => {
    const noDelete = ('aliases' in snippet && field === 'canonical' && snippet.aliases.length > 0)
      || field === 'value'
    const { state, dispatch } = useContext(SettingsContext)
    const { appDispatch } = useContext(AppContext)
    const baseValue: string = index !== undefined && 'aliases' in snippet
        ? snippet.aliases[index]
        : field in snippet
            ? snippet[field]
            : 'Error!'
    const modified = state.snippetsPending?.modify?.[snippetModifyKey(snippet, baseValue)]
    const savedValue: string = modified && 'newValue' in modified ? modified.newValue : baseValue
    const modifyAction = (newValue: string): SettingsAction => ({
        type: 'CACHE_MODIFY_SNIPPET',
        original: snippet,
        field,
        oldValue: savedValue,
        newValue
    })
    const deleteAction: SettingsAction = {
        type: 'DELETE_SNIPPET',
        snippet,
        value: savedValue
    }
    const [isModifying, setIsModifying] = useState(savedValue === '')
    const isSavingModification = !!state.snippetsPending?.modify?.[snippetModifyKey(snippet, baseValue)]
    const showDelete = !noDelete && !isModifying && !isSavingModification && !snippet.creating
    const isDeleting = actionInProgress(deleteAction, state)
    const isRegex = savedValue.replace(/ [(]?[?]{3}[)]?/, '').match(/[[\]*+?]|(\\[bB])/)
    return <>{isModifying
        ? <SnippetInput className={ snippet.type } setIsActive={ setIsModifying } saveAction={ modifyAction } savedValue={ savedValue } />
        : <span className={ isRegex ? 'code' : undefined } onClick={ () => setIsModifying(true) }>{savedValue || <em>none</em>}</span>}
    { isRegex && !isModifying && <a
        className="regex-help"
        href="https://www.regular-expressions.info/"
        rel="noreferrer"
        target="_blank"
        onMouseOver={ () => appDispatch({ type: 'SET_NOTIFICATION_TEXT', message: regexHelpMessage, icon: 'circle-question' }) }
        onMouseOut={ () => appDispatch({ type: 'CLEAR_NOTIFICATION_TEXT', message: regexHelpMessage }) }>
        <FontAwesomeIcon icon="circle-question" />
    </a> }
    { !isDeleting && !isModifying && !snippet.creating && <FontAwesomeIcon
        className="clickable"
        icon={ isSavingModification ? 'spinner' : 'pen' }
        spin={ isSavingModification }
        onClick={ () => setIsModifying(true) }
    /> }
    { snippet.creating && <FontAwesomeIcon className="clickable" spin={ true } icon="spinner" />}
    { showDelete && <FontAwesomeIcon
        className="clickable"
        spin={ isDeleting }
        icon={ isDeleting ? 'spinner' : 'xmark' }
        onClick={ () => conditionalDispatch(deleteAction, state, dispatch) } />}
    </>
}

const SnippetInput = (props: {
    className: string
    setIsActive: (isActive: boolean) => void
    savedValue?: string
    placeholder?: string
    completions?: string[]
    saveAction: (value: string) => SettingsAction
}): ReactElement => {
    const { appDispatch } = useContext(AppContext)
    const { dispatch } = useContext(SettingsContext)
    const [value, setValue] = useState(props.savedValue || '')
    const [typed, setTyped] = useState(props.savedValue || '')
    const inputRef = useRef<HTMLInputElement>(null)
    const [selectFrom, setSelectFrom] = useState<number | undefined>(undefined)
    // After a type-ahead completion, select the auto-filled tail so typing overwrites it.
    useEffect(() => {
        if (selectFrom !== undefined && inputRef.current) {
            inputRef.current.setSelectionRange(selectFrom, value.length)
            setSelectFrom(undefined)
        }
    }, [selectFrom, value])
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value
        const extending = input.length > typed.length
        setTyped(input)
        const match = extending && props.completions
            ?.find(option => option.length > input.length
              && option.toLowerCase().startsWith(input.toLowerCase()))
        if (match) {
            setValue(match)
            setSelectFrom(input.length)
        } else {
            setValue(input)
        }
    }
    const saveModification = () => {
        if (value && value !== props.savedValue) {
            dispatch(props.saveAction(value))
        }
        props.setIsActive(false)
    }
    const cancelModification = () => {
        setValue(props.savedValue || '')
        props.setIsActive(false)
        appDispatch({ type: 'SET_SHORTCUTS_ACTIVE', active: true })
    }
    return <>
        <input
            ref={ inputRef }
            className={ `snippet-${props.className.replaceAll('.', '-')}` }
            autoFocus
            value={ value }
            placeholder={ props.placeholder }
            onKeyDown={ onModifyKeyDown(saveModification, cancelModification) }
            onFocus={ () => appDispatch({ type: 'SET_SHORTCUTS_ACTIVE', active: false }) }
            onBlur={ cancelModification /* NB if we click the Save icon, the input disappears and never calls onBlur. */ }
            onChange={ onChange } />
        {' '}<FontAwesomeIcon className="clickable" icon="check" onMouseDown={ saveModification } />
        {' '}<FontAwesomeIcon className="clickable" icon="xmark" onClick={ () => props.setIsActive(false) } />
    </>
}

const AddSnippetButton = ({ identifier, type, canonical }:
{ identifier: SnippetIdentifier, type: string, canonical?: string }
): ReactElement => {
    const { state } = useContext(SettingsContext)
    const [isActive, setIsActive] = useState(false)
    const saveAction = (value: string): SettingsAction =>
        ({ type: 'CACHE_CREATE_SNIPPET',
            snippet: identifier == 'aliases'
                ? canonical
                    ? { type, canonical, aliases: (state.snippets?.aliases[type]?.[canonical].aliases || []).concat([value]) }
                    : { type, canonical: value, aliases: [] }
                : identifier === 'attributes'
                    ? { type, key: value, value: '' }
                    : { type, str: value }
        })
    if ('snippetsPending' in state && canonical && state.snippetsPending?.create?.[type]?.[canonical]) {
        return <FontAwesomeIcon icon="spinner" spin={ true } className="clickable" />
    }
    return <div className="add-snippet">
        {isActive
            ? <SnippetInput
                className={ type }
                setIsActive={ setIsActive }
                placeholder={ snippetsConfig[identifier]?.types?.[type].placeholder }
                saveAction={ saveAction } />
            : <div className="clickable" onClick={ () => setIsActive(true) }><FontAwesomeIcon icon="plus" />
                {' '}{ canonical ? 'Alias' : snippetsConfig[identifier].new }
            </div>}
    </div>
}

// Shared group-editing state for both controls: the current (pending-aware) group and the
//  action that persists a change through the generic modify flow.
const useGroupEditor = (snippet: GenericSnippet) => {
    const { state, dispatch } = useContext(SettingsContext)
    const [isEditing, setIsEditing] = useState(false)
    const group = snippetGroup(snippet, state)
    const modifyGroup = (value: string): SettingsAction => ({
        type: 'CACHE_MODIFY_SNIPPET',
        original: snippet,
        field: 'groupLabel',
        oldValue: group,
        newValue: value
    })
    return { state, dispatch, isEditing, setIsEditing, group, modifyGroup }
}

const GroupEditInput = (props: {
    className: string
    group: string
    completions: string[]
    setIsEditing: (active: boolean) => void
    saveAction: (value: string) => SettingsAction
}): ReactElement =>
    <span className={ `${props.className} editing` }>
        <SnippetInput
            className="group"
            setIsActive={ props.setIsEditing }
            savedValue={ props.group }
            placeholder="Group"
            completions={ props.completions }
            saveAction={ props.saveAction } />
    </span>

// Compact group control: no persistent name shown, since the section header carries it.
const SnippetGroupControl = ({ snippet }: { snippet: GenericSnippet }): ReactElement => {
    const { state, dispatch, isEditing, setIsEditing, group, modifyGroup } = useGroupEditor(snippet)
    if (isEditing) {
        return <GroupEditInput
            className="snippet-group-edit"
            group={ group }
            completions={ existingGroups(state.snippets?.snippets) }
            setIsEditing={ setIsEditing }
            saveAction={ modifyGroup } />
    }
    return <FontAwesomeIcon
        className="clickable snippet-group-icon"
        icon={ group ? 'folder-minus' : 'folder-plus' }
        onClick={ group ? () => dispatch(modifyGroup('')) : () => setIsEditing(true) } />
}

const Snippet = ({ snippet }: { snippet: SnippetType }): ReactElement => <>
    <div className="snippet"><ModifiableSnippet snippet={ snippet } field="str" /><SnippetGroupControl snippet={ snippet } /></div>
</>

// TODO to modify this, use a colour-picker widget
const Attribute = ({ attribute }: { attribute: AttributeType }): ReactElement => {
    const flag = attribute.type === 'country.colours' ? countryFlag(attribute.key) : ''
    return <>
        <div className="attribute">{flag && <>{flag}{' '}</>}<ModifiableSnippet snippet={ attribute } field="key" /> - {
            attribute.type === 'country.colours'
                ? <div style={ { color: attribute.value } }><ModifiableSnippet snippet={ attribute } field="value" /></div>
                : <ModifiableSnippet snippet={ attribute } field="value" />}
        </div>
    </>
}

const existingGroups = (collection: { [type: string]: { [key: string]: GenericSnippet } } = {}): string[] => {
    const groups = new Set<string>()
    Object.values(collection).forEach(byKey =>
        Object.values(byKey).forEach(snippet => 'groupLabel' in snippet && snippet.groupLabel && groups.add(snippet.groupLabel)))
    return [...groups].sort()
}

const GroupControl = ({ alias }: { alias: AliasType }): ReactElement => {
    const { state, dispatch, isEditing, setIsEditing, group, modifyGroup } = useGroupEditor(alias)
    if (isEditing) {
        return <GroupEditInput
            className="alias-group-edit"
            group={ group }
            completions={ existingGroups(state.snippets?.aliases) }
            setIsEditing={ setIsEditing }
            saveAction={ modifyGroup } />
    }
    return <span className="alias-group-edit">
        <span
            className={ group ? 'group-name' : 'group-name group-none' }
            onClick={ () => setIsEditing(true) }>{group || 'Assign group'}
        </span>
        {group && <FontAwesomeIcon
            className="clickable group-clear"
            icon="xmark"
            onClick={ () => dispatch(modifyGroup('')) } />}
        <FontAwesomeIcon
            className={ group ? 'clickable group-icon grouped' : 'clickable group-icon' }
            icon={ group ? 'folder' : 'folder-plus' }
            onClick={ () => setIsEditing(true) } />
    </span>
}

const Alias = ({ alias }: { alias: AliasType }): ReactElement => {
    const flag = alias.type === 'countries' ? countryFlag(alias.canonical) : ''
    const canonical = <div className="alias-canonical">
        {flag && <>{flag}{' '}</>}<ModifiableSnippet snippet={ alias } field="canonical" />
    </div>
    const addAlias = <AddSnippetButton identifier="aliases" type={ alias.type } canonical={ alias.canonical } />
    return <div className="alias">
        <GroupControl alias={ alias } />
        {alias.aliases.length === 0
            ? <>{canonical} <em>can be matched.</em> {addAlias}</>
            : <>
                {canonical}
                {alias.aliases.map((_, i) => <div key={ alias.aliases[i] } className="alias-alias">
                    <ModifiableSnippet snippet={ alias } field="alias" index={ i } />
                </div>)}
                { addAlias }
            </>}
        <div className="clear-float" />
    </div>
}

export default SnippetsPage
